import useCollapsedLayers, {
  useCollapsedActions,
} from '@components/MainStage/collapsedLayers.store';
import useAbleCollapseLayer from '@components/MainStage/hooks/useAbleCollapseLayer';
import { canvasState } from '@components/MainStage/store';
import { LayerTypes, SubLayerTypes } from '@constants/canvas/layers';
import { LoadingKeys } from '@constants/entities/ui';
import { useProject } from '@context/Project';
import useLoading from '@hooks/useLoading';
import CanvasModelRenderer from '@utils/canvas/CanvasModelRenderer';
import { collapseRelationController } from '@utils/canvas/CollapseRelationController';
import LayerView from '@utils/canvas/LayerView';
import { toggleCollapsedNodes } from '@utils/canvasHelpers';
import { filterConnectedNodes } from '@views/Insights/Stage/helpers';
import { Tabs } from '@views/Insights/types';

const useCollapseController = () => {
  const { collapse, expand } = useCollapsedActions();
  const { beginLoading } = useLoading();
  const { insightsTab } = useProject();
  const { isCollapsed } = useCollapsedLayers();

  const { isAbleToCollapse, handleUnableCollapse } = useAbleCollapseLayer();

  const {
    toolbox: { mode },
  } = useProject();

  const hideLayer = async (id: LayerTypes | SubLayerTypes) => {
    if (!mode) return;

    const { nodes, setNodes } = canvasState();

    const hiddenNodes = toggleCollapsedNodes({
      collapseIds: [id] as LayerTypes[],
      isCollapsed: true,
      nodes,
    });

    setNodes(hiddenNodes);

    await CanvasModelRenderer.redrawFullCanvasLayers(mode!);
  };

  const expandLayer = async (
    ids: (LayerTypes | SubLayerTypes)[],
  ): Promise<void> => {
    if (!ids || !mode) return Promise.resolve();

    return new Promise((resolve) => {
      const { nodes, edges, setNodes, setEdges, getActiveNode } = canvasState();

      const shownNodes = toggleCollapsedNodes({
        collapseIds: ids as LayerTypes[],
        isCollapsed: false,
        nodes,
      });

      const selectedNode = getActiveNode();

      if (insightsTab === Tabs.FocusOn && selectedNode) {
        const { connectedNodes, connectedEdges } = filterConnectedNodes(
          shownNodes,
          edges,
          selectedNode.id,
        );

        setNodes(connectedNodes);
        setEdges(connectedEdges);
      } else {
        setNodes(shownNodes);
      }

      const settle = () => resolve(undefined);
      const redraw = () =>
        CanvasModelRenderer.redrawFullCanvasLayers(mode!).then(settle, settle);

      if (ids.some((id) => LayerView.isLayer(id))) {
        setTimeout(redraw, 0);
      } else {
        redraw();
      }
    });
  };

  const handleToggleLayer = async (id: SubLayerTypes | LayerTypes) => {
    if (!mode) return;

    const endLoading = beginLoading(LoadingKeys.Collapse);

    try {
      if (isCollapsed(id)) {
        expand(id as LayerTypes);
        await expandLayer([id]);

        await collapseRelationController.renderPhantoms();

        return;
      }

      if (isAbleToCollapse(id)) {
        collapse(id as LayerTypes);
        await hideLayer(id);

        await collapseRelationController.renderPhantoms();

        return;
      }

      handleUnableCollapse();
    } finally {
      endLoading();
    }
  };

  return { handleToggleLayer, hideLayer, expandLayer };
};

export default useCollapseController;
